import { Link, useRouterState } from '@tanstack/react-router';
import classNames from 'classnames';
import { type ComponentProps, Fragment, type ReactNode } from 'react';

export type NavItemChildItem = {
  to: string;
  label: string;
  'data-testid'?: string;
  /** Rendered instead of the link, for a child that doesn't navigate. */
  element?: ReactNode;
};

type Props = Omit<ComponentProps<typeof Link>, 'children'> & {
  children?: ReactNode;
  childItems?: NavItemChildItem[];
  showChildren?: boolean;
};

// childItems live OUTSIDE the outer <Link> to avoid nested <a><a></a></a> (React 19 hydration error).
export default function NavItem({ to, childItems, children, showChildren = true, ...rest }: Props) {
  const hasChildren = childItems && childItems.length > 0;
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const toStr = typeof to === 'string' ? to : '';
  const isActive = !!toStr && (pathname === toStr || pathname.startsWith(`${toStr}/`));

  return (
    <div className={classNames(hasChildren ? 'group/parent' : 'group/child', isActive && 'active')}>
      <Link
        to={to}
        className={classNames('block font-noto text-sm font-normal text-grey-90 rounded-sm transition-all focus-ring')}
        {...rest}
      >
        <div
          className={classNames(
            'px-2 py-1 rounded-sm transition-all',
            'hover:bg-grey-20',
            hasChildren && 'group-[.active]/parent:bg-blue-10',
            !hasChildren && 'group-[.active]/child:bg-blue-10',
          )}
        >
          <span
            className={classNames(
              'text-grey-90',
              hasChildren && 'group-[.active]/parent:text-blue-60 group-[.active]/parent:font-medium',
              !hasChildren && 'group-[.active]/child:text-blue-60 group-[.active]/child:font-medium',
            )}
          >
            {children}
          </span>
        </div>
      </Link>
      {showChildren && hasChildren && (
        <div className="ml-4 hidden py-1 space-y-1 group-[.active]/parent:block">
          {childItems.map(({ to: childTo, label, 'data-testid': childTestId, element }) =>
            element ? (
              <Fragment key={childTo}>{element}</Fragment>
            ) : (
              <Link
                key={childTo}
                to={childTo}
                className={classNames(
                  'font-noto text-sm font-normal text-grey-90 flex px-2 py-1 rounded-[6px] transition-all',
                  'hover:bg-grey-20 hover:text-primary-dark',
                  'focus-ring',
                )}
                activeProps={{ className: '!bg-blue-10 !text-blue-60 font-medium' }}
                {...(childTestId ? { 'data-testid': childTestId } : {})}
              >
                {label}
              </Link>
            ),
          )}
        </div>
      )}
    </div>
  );
}
